import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Circle, Loader2 } from 'lucide-react';
import { notificationsAPI } from '../../services/api';
import { Notification as AppNotification } from '../../types';
import { formatServerDate } from '../../utils/datetime';
import { getIconForType, getLinkForType } from './notificationsHelpers';

interface Props {
  /** Called when the user dismisses the popover (outside click, Esc, item click). */
  onClose: () => void;
  /** Bubbled to Layout so the header badge stays in sync with mark-as-read /
   *  mark-all actions inside the popover without a polling round-trip. */
  onCountChange: (count: number) => void;
  /** Optional ref to the trigger (Bell) button. Clicks landing inside this
   *  element are excluded from the outside-click dismissal so the button's
   *  own onClick toggle isn't sandwiched between a close-then-reopen race —
   *  without this, a second tap on the Bell closes the popover via
   *  outside-click, then the button's `setOpen(o => !o)` flips it back on
   *  and the popover never closes from the trigger. */
  triggerRef?: React.RefObject<HTMLElement>;
}

/**
 * Plan-NotificationsHeaderPopover.md §6 — compact notifications list rendered
 * inside the global Layout header. Top 20 items, mark-as-read on click, mark-
 * all action, footer link to /inbox?tab=notifications for the full list.
 *
 * Email types navigate to /inbox here (no access to the Inbox tab's
 * `onEmailNotificationClick` handler from this surface). NotificationsTab
 * inside Inbox keeps the in-place thread-switching behavior; documented as
 * a known minor regression in §6 / §12 of the plan.
 */
const NotificationsPopover: React.FC<Props> = ({ onClose, onCountChange, triggerRef }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  // Initial load — also seeds the badge with the latest unread count
  // (mark-all + per-item mark-as-read inside the popover then keep it in
  // sync without a second round-trip).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await notificationsAPI.list(undefined);
        if (cancelled) return;
        const data = res.data ?? [];
        setNotifications(data.slice(0, 20));
        onCountChange(data.filter((n) => !n.is_read).length);
      } catch {
        // Non-fatal — empty list + zero badge is the safest fallback.
        if (!cancelled) {
          setNotifications([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // onCountChange is stable enough for our purposes — pulling it into
    // deps would re-fetch on every Layout render. Mount-only fetch matches
    // the plan §6 contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click-outside + Esc dismissal. mousedown (not click) so the dismissal
  // fires before any focused control inside the popover would steal it.
  // Clicks on the trigger (Bell button) are excluded — otherwise a second
  // tap on the Bell would close here via outside-click and then the button's
  // own toggle (`setOpen(o => !o)`) would flip it back on. Net: popover
  // wouldn't close from the trigger. Symmetric with the a11y popover
  // pattern in Layout.tsx, which wraps button + popover in a single ref.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current && popoverRef.current.contains(target)) return;
      if (triggerRef?.current && triggerRef.current.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, triggerRef]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsAPI.markAsRead(id);
    } catch {
      // Optimistic update below still applies — the next 60s poll in
      // Layout (or a popover reopen) will reconcile if the request flopped.
    }
    setNotifications((prev) => {
      const next = prev.map((n) => (n.id === id ? { ...n, is_read: true } : n));
      onCountChange(next.filter((n) => !n.is_read).length);
      return next;
    });
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
    } catch {
      // Same rationale as handleMarkAsRead — optimistic, polling reconciles.
    }
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    onCountChange(0);
  };

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Notifications"
      className="fixed left-2 right-2 top-20 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-w-[calc(100vw-1rem)] z-50 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl max-h-[70vh] flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Notifications</h3>
        <button
          onClick={handleMarkAllAsRead}
          className="text-xs text-primary hover:underline"
        >
          Mark all read
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-5 h-5 animate-spin text-primary mx-auto" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">You're all caught up!</p>
          </div>
        ) : (
          notifications.map((n) => (
            <Link
              key={n.id}
              to={n.metadata?.link || getLinkForType(n.type)}
              onClick={() => {
                if (!n.is_read) handleMarkAsRead(n.id);
                onClose();
              }}
              className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                n.is_read ? '' : 'bg-primary/5 dark:bg-primary/10'
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">{getIconForType(n.type)}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{n.title}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 mt-0.5">{n.message}</p>
                <span className="text-[10px] text-gray-400 mt-1 block">
                  {formatServerDate(n.created_at)}
                </span>
              </div>
              {!n.is_read && (
                <Circle className="w-2 h-2 fill-current text-primary mt-1.5 flex-shrink-0" />
              )}
            </Link>
          ))
        )}
      </div>

      {/* Footer */}
      <Link
        to="/inbox?tab=notifications"
        onClick={onClose}
        className="block text-center px-4 py-3 text-sm font-medium text-primary hover:bg-gray-50 dark:hover:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shrink-0"
      >
        View all in Inbox
      </Link>
    </div>
  );
};

export default NotificationsPopover;
